import { CLIENT_TYPE_LABELS, formatCad, type InvoiceView } from "@shared/modules/x-atelier";
import { Badge } from "../../../core/components/ui/badge";
import { Icon } from "../../../core/components/ui/icon";
import { formatDayShort } from "../lib/dates";
import { InvoiceStatusBadge } from "./invoice-status-badge";

interface InvoiceRowProps {
  invoice: InvoiceView;
  onOpen: (invoice: InvoiceView) => void;
  /** Masqué dans l'état de compte d'un client (le nom est déjà en titre). */
  showClient?: boolean;
}

/**
 * Rangée du registre : numéro et date d'émission, partie facturée et
 * véhicule, total et dû, statut avec l'échéance. Une cible tactile pleine
 * largeur qui ouvre la fiche de la facture.
 */
export function InvoiceRow({ invoice: v, onOpen, showClient = true }: InvoiceRowProps) {
  const late = v.status === "en_souffrance";
  const classes = [
    "invoice-row",
    late && "invoice-row--late",
    v.status === "annulee" && "invoice-row--void",
  ]
    .filter(Boolean)
    .join(" ");
  const sub = [
    v.vehicleLabel ?? v.appointmentTitle,
    v.vehicleOwnerName ? `pour ${v.vehicleOwnerName}` : null,
    `RDV ${formatDayShort(v.appointmentStartAt)}`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <button type="button" className={classes} onClick={() => onOpen(v)}>
      <span className="invoice-row__id">
        <span className="invoice-row__number">{v.number}</span>
        <span className="invoice-row__date">{formatDayShort(v.issuedAt)}</span>
      </span>
      <span className="invoice-row__main">
        <span className="invoice-row__title">
          {showClient ? v.clientName : (v.vehicleLabel ?? v.appointmentTitle)}
          {showClient && v.clientType !== "particulier" ? (
            <Badge variant="primary">{CLIENT_TYPE_LABELS[v.clientType]}</Badge>
          ) : null}
        </span>
        <span className="invoice-row__sub">
          {showClient ? sub : `RDV ${formatDayShort(v.appointmentStartAt)}`}
        </span>
      </span>
      <span className="invoice-row__amount">
        <span className="invoice-row__total">{formatCad(v.totalWithTaxCents)}</span>
        {v.status === "annulee" ? (
          <small>annulée</small>
        ) : v.dueCents > 0 ? (
          <small className={late ? "is-late" : undefined}>{formatCad(v.dueCents)} dû</small>
        ) : (
          <small className="is-paid">payée</small>
        )}
      </span>
      <span className="invoice-row__status">
        <InvoiceStatusBadge status={v.status} overdueDays={v.overdueDays} />
        {v.status !== "annulee" && v.status !== "payee" ? (
          <small>Échéance {formatDayShort(v.dueAt)}</small>
        ) : null}
      </span>
      <span className="invoice-row__chevron" aria-hidden="true">
        <Icon name="chevron-right" size={18} />
      </span>
    </button>
  );
}
